import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  Checkbox,
  Modal,
  Button,
  Spin,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import locale from "antd/locale/es_ES";
import {
  empleadosApi,
  tiposNovedadesApi,
  novedadesApi,
} from "../../services/api";
import moment from "moment";
import { toast } from "react-toastify";

// Hooks personalizados
const useFormData = () => {
  const [empleados, setEmpleados] = useState([]);
  const [tiposNovedad, setTiposNovedad] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [respEmpleados, respTipos] = await Promise.all([
        empleadosApi.getAll(),
        tiposNovedadesApi.getAll()
      ]);
      
      if (respEmpleados.data.status) {
        setEmpleados(respEmpleados.data.empleados || []);
      }
      if (respTipos.data.status) {
        setTiposNovedad(respTipos.data.tipos_novedades || []);
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      toast.error("Error al cargar datos necesarios");
    } finally {
      setLoading(false);
    }
  }, []);

  return { empleados, tiposNovedad, loading, cargarDatos };
};

const useEmpleadoData = (form) => {
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [esAdministrativo, setEsAdministrativo] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleEmpleadoChange = useCallback(async (empleadoId) => {
    if (!empleadoId) {
      setEmpleadoSeleccionado(null);
      form.setFieldsValue({
        campus: "", cedula: "", cargo: "", dependencia: ""
      });
      return;
    }

    if (empleadoSeleccionado?.id === empleadoId) return;

    setLoading(true);
    try {
      const resp = await empleadosApi.getOne(empleadoId);
      if (resp.data.status) {
        const empleado = resp.data.empleado;
        setEmpleadoSeleccionado(empleado);
        setEsAdministrativo(empleado.tipo_empleado?.tipo_empleado === "Administrativo");
        
        form.setFieldsValue({
          campus: empleado.campus.campus,
          cedula: empleado.cedula,
          cargo: empleado.cargo,
          dependencia: empleado.dependencia.dependencia,
        });
      }
    } catch (error) {
      console.error("Error al cargar datos del empleado:", error);
      toast.error("Error al cargar información del empleado");
    } finally {
      setLoading(false);
    }
  }, [empleadoSeleccionado, form]);

  return { 
    empleadoSeleccionado, 
    esAdministrativo, 
    loading, 
    handleEmpleadoChange 
  };
};

// Hook para cargar datos completos de la novedad
const useNovedadCompleta = () => {
  const [novedadCompleta, setNovedadCompleta] = useState(null);
  const [cargandoNovedad, setCargandoNovedad] = useState(false);
  const cargarNovedadCompleta = useCallback(async (novedadId) => {
    if (!novedadId) return;
    
    console.log('Cargando novedad completa con ID:', novedadId);
    setCargandoNovedad(true);
    try {
      const response = await novedadesApi.getOne(novedadId);
      if (response.data.status) {
        console.log('Novedad completa cargada:', response.data.novedad);
        setNovedadCompleta(response.data.novedad);
        return response.data.novedad;
      }
    } catch (error) {
      console.error("Error al cargar novedad completa:", error);
      message.error("Error al cargar los datos de la novedad");
    } finally {
      setCargandoNovedad(false);
    }
  }, []);

  return { novedadCompleta, cargandoNovedad, cargarNovedadCompleta };
};

// Utilidades
const prepararDatosFormulario = (values, fechaRango, archivo, subirSoporte, modoEdicion, novedadEditar) => {
  console.log('Preparando datos del formulario:', {
    values,
    fechaRango,
    subirSoporte,
    modoEdicion,
    novedadEditar
  });  // Preparar datos base
  const datos = {
    empleado_id: parseInt(values.empleado),
    fecha: modoEdicion ? 
      (novedadEditar?.fecha || moment().format("YYYY-MM-DD")) : 
      moment().format("YYYY-MM-DD"),
    tipo_novedad_id: parseInt(values.tipoNovedad),
    dias: parseInt(values.dias) || 0,
    horas: parseInt(values.hrs) || 0,
    minutos: parseInt(values.mins) || 0,
    nomina: Boolean(values.nomina), // Convertir a boolean
    observaciones: values.observaciones || "",
    curso: values.curso || "",
    horario: values.horario || "",
    grupo: values.grupo || "",
    soporte: Boolean(subirSoporte), // Convertir a boolean
    usuario_id: parseInt(JSON.parse(localStorage.getItem("user"))?.id)
  };

  // Agregar fechas si existen
  if (fechaRango?.[0]) {
    datos.fecha_ini = fechaRango[0].format("YYYY-MM-DD");
  }
  if (fechaRango?.[1]) {
    datos.fecha_fin = fechaRango[1].format("YYYY-MM-DD");
  }

  console.log('Datos preparados:', datos);  // Si hay archivo, usar FormData
  if (archivo) {
    const formData = new FormData();
    
    // Para FormData, convertir boolean a enteros (0 o 1) que Laravel acepta para boolean
    const datosParaFormData = {
      ...datos,
      nomina: datos.nomina ? 1 : 0,
      soporte: datos.soporte ? 1 : 0
    };
    
    Object.entries(datosParaFormData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    
    formData.append("archivo", archivo);
    
    console.log('Enviando como FormData con archivo - boolean como enteros');
    return formData;
  }

  // Si no hay archivo, enviar como JSON
  console.log('Enviando como JSON sin archivo');
  return datos;
};

const configurarFormularioEdicion = (form, novedadEditar, empleados, tiposNovedad) => {
  console.log('Configurando formulario con:', { 
    novedadEditar, 
    empleados: empleados.length, 
    tipos: tiposNovedad.length,
    fechas: {
      fecha_ini: novedadEditar.fecha_ini,
      fecha_fin: novedadEditar.fecha_fin
    }
  });
  
  // Obtener IDs correctos dependiendo de la estructura de datos
  const tipoNovedadId = novedadEditar.tipo_novedad_id || 
    tiposNovedad.find((tipo) => tipo.novedad === novedadEditar.tipo_novedad?.novedad)?.id;

  const empleadoId = novedadEditar.empleado_id || 
    empleados.find((emp) => emp.nombre === novedadEditar.empleado?.nombre)?.id;

  console.log('IDs encontrados:', { empleadoId, tipoNovedadId });
  
  // Preparar fechas con parsing correcto
  let fechaRango = null;
  if (novedadEditar.fecha_ini && novedadEditar.fecha_fin && 
      novedadEditar.fecha_ini !== "N/A" && novedadEditar.fecha_fin !== "N/A") {
      console.log('Parseando fechas:', { 
      fecha_ini: novedadEditar.fecha_ini, 
      fecha_fin: novedadEditar.fecha_fin 
    });
    
    // Parsear fechas en formato YYYY-MM-DD (ISO estándar)
    const fechaInicial = moment(novedadEditar.fecha_ini, "YYYY-MM-DD", true);
    const fechaFinal = moment(novedadEditar.fecha_fin, "YYYY-MM-DD", true);

    console.log('Fechas parseadas:', {
      fechaInicial: { valor: fechaInicial.format(), valida: fechaInicial.isValid() },
      fechaFinal: { valor: fechaFinal.format(), valida: fechaFinal.isValid() }
    });
    
    if (fechaInicial.isValid() && fechaFinal.isValid()) {
      fechaRango = [fechaInicial, fechaFinal];
    }
  }

  form.setFieldsValue({
    empleado: empleadoId,
    tipoNovedad: tipoNovedadId,
    campus: novedadEditar.empleado?.campus?.campus || novedadEditar.campus,
    cedula: novedadEditar.empleado?.cedula || novedadEditar.cedula,
    cargo: novedadEditar.empleado?.cargo || novedadEditar.cargo,
    dependencia: novedadEditar.empleado?.dependencia?.dependencia || novedadEditar.dependencia,
    fechaRango,
    mins: novedadEditar.minutos !== undefined ? parseInt(novedadEditar.minutos) : 
          (novedadEditar.mins !== "N/A" ? parseInt(novedadEditar.mins) : 0),
    hrs: novedadEditar.horas !== undefined ? parseInt(novedadEditar.horas) : 
         (novedadEditar.hrs !== "N/A" ? parseInt(novedadEditar.hrs) : 0),
    dias: novedadEditar.dias !== undefined ? parseInt(novedadEditar.dias) : 
          (novedadEditar.dias !== "N/A" ? parseInt(novedadEditar.dias) : 0),
    nomina: novedadEditar.nomina,
    observaciones: novedadEditar.observaciones && novedadEditar.observaciones !== "Sin observaciones" 
      ? novedadEditar.observaciones : "",
    curso: novedadEditar.curso || "",
    horario: novedadEditar.horario || "",
    grupo: novedadEditar.grupo || "",
  });

  return { empleadoId, fechaRango };
};

// Componentes
const EmpleadoFields = ({ form, empleados, handleEmpleadoChange, modoEdicion }) => (
  <>
    <Form.Item
      name="empleado"
      label="Empleado"
      rules={[{ required: true, message: "Por favor seleccione el empleado" }]}
    >
      <Select
        placeholder="Seleccione el empleado"
        onChange={handleEmpleadoChange}
        showSearch
        optionFilterProp="children"
        disabled={modoEdicion}
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {empleados.map((emp) => (
          <Select.Option key={emp.id} value={emp.id}>
            {emp.nombre}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
    
    <Form.Item name="campus" label="Campus">
      <Input disabled className="!text-gray-500" />
    </Form.Item>
    
    <Form.Item name="cedula" label="Cédula">
      <Input disabled className="!text-gray-500" />
    </Form.Item>
    
    <Form.Item name="cargo" label="Cargo">
      <Input disabled className="!text-gray-500" />
    </Form.Item>
    
    <Form.Item name="dependencia" label="Dependencia">
      <Input disabled className="!text-gray-500" />
    </Form.Item>
  </>
);

const NovedadFields = ({ tiposNovedad, handleFechasChange }) => (
  <>
    <Form.Item
      name="fechaRango"
      label="Periodo de la Novedad"
      rules={[{ required: true, message: "Por favor seleccione el periodo de la novedad" }]}
    >
      <DatePicker.RangePicker
        className="w-full"
        format="DD/MM/YYYY"
        locale={locale}
        onChange={handleFechasChange}
      />
    </Form.Item>
    
    <Form.Item
      name="tipoNovedad"
      label="Tipo Novedad"
      rules={[{ required: true, message: "Por favor seleccione el tipo de novedad" }]}
    >
      <Select
        placeholder="Seleccione el tipo de novedad"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {tiposNovedad.map((tipo) => (
          <Select.Option key={tipo.id} value={tipo.id}>
            {tipo.novedad}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  </>
);

const TiempoFields = () => (
  <div className="flex gap-4">
    <Form.Item name="mins" label="Minutos" className="w-1/3">
      <InputNumber min={0} max={59} className="w-full" />
    </Form.Item>
    <Form.Item name="hrs" label="Horas" className="w-1/3">
      <InputNumber min={0} max={23} className="w-full" />
    </Form.Item>
    <Form.Item name="dias" label="Días" className="w-1/3">
      <InputNumber min={0} className="w-full" />
    </Form.Item>
  </div>
);

const CursoFields = ({ esAdministrativo }) => {
  if (esAdministrativo) return null;
  
  return (
    <div className="col-span-2 grid grid-cols-3 gap-4">
      <Form.Item name="curso" label="Curso (Opcional)">
        <Input />
      </Form.Item>
      <Form.Item name="horario" label="Horario (Opcional)">
        <Input placeholder="Ej: Lunes y miércoles 14:00-16:00" />
      </Form.Item>
      <Form.Item name="grupo" label="Grupo (Opcional)">
        <Input placeholder="Ej: ADB-2025" />
      </Form.Item>
    </div>
  );
};

const CheckboxFields = ({ setSubirSoporte }) => (
  <div className="col-span-2 grid grid-cols-4 gap-4">
    <Form.Item name="nomina" valuePropName="checked" className="flex items-center">
      <Checkbox>Afecta Nómina (Opcional)</Checkbox>
    </Form.Item>
    <Form.Item name="soporte" valuePropName="checked" className="flex items-center">
      <Checkbox onChange={(e) => setSubirSoporte(e.target.checked)}>
        Incluye Soporte (Opcional)
      </Checkbox>
    </Form.Item>
  </div>
);

const SoporteUpload = ({ subirSoporte, setArchivo }) => {
  if (!subirSoporte) return null;
  
  return (
    <Form.Item
      label="Archivo de Soporte"
      name="archivoSoporte"
      rules={[{ required: true, message: "Por favor adjunte un archivo de soporte" }]}
    >
      <Upload
        beforeUpload={(file) => {
          const isValidType = [
            "application/pdf", "image/png", "image/jpeg", "image/jpg",
            "application/msword", 
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ].includes(file.type);
          const isValidSize = file.size / 1024 / 1024 < 2;

          if (!isValidType) {
            message.error("Solo se permiten archivos PDF, PNG, JPG, DOC o DOCX");
          }
          if (!isValidSize) {
            message.error("El archivo debe ser menor a 2MB");
          }
          if (isValidType && isValidSize) {
            setArchivo(file);
          }
          return false;
        }}
        maxCount={1}
        onRemove={() => setArchivo(null)}
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
      >
        <Button icon={<UploadOutlined />}>Seleccionar archivo</Button>
      </Upload>
    </Form.Item>
  );
};

// Componente principal
const FormNovedad = ({ visible, onCancel, onSuccess, novedadEditar }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fechaRango, setFechaRango] = useState(null);
  const [subirSoporte, setSubirSoporte] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);

  const { empleados, tiposNovedad, loading, cargarDatos } = useFormData();
  const { 
    empleadoSeleccionado, 
    esAdministrativo, 
    loading: empleadoLoading, 
    handleEmpleadoChange 
  } = useEmpleadoData(form);
  const { novedadCompleta, cargandoNovedad, cargarNovedadCompleta } = useNovedadCompleta();
  // Efectos
  useEffect(() => {
    if (visible) {
      cargarDatos();
      form.resetFields();
      
      if (novedadEditar && novedadEditar.id) {
        setModoEdicion(true);
        // Cargar datos completos de la novedad desde el API
        cargarNovedadCompleta(novedadEditar.id);
      } else {
        setModoEdicion(false);
        setSubirSoporte(false);
        setFechaRango(null);
        setArchivo(null);
      }
    }
  }, [visible, novedadEditar, cargarDatos, cargarNovedadCompleta]);

  // Configurar formulario cuando se carguen los datos completos
  useEffect(() => {
    if (visible && novedadCompleta && tiposNovedad.length > 0 && empleados.length > 0) {
      const { empleadoId } = configurarFormularioEdicion(form, novedadCompleta, empleados, tiposNovedad);
      setSubirSoporte(novedadCompleta.soporte);
      
      if (empleadoId) {
        handleEmpleadoChange(empleadoId);
      }
        // Configurar fechas si existen
      if (novedadCompleta.fecha_ini && novedadCompleta.fecha_fin && 
          novedadCompleta.fecha_ini !== "N/A" && novedadCompleta.fecha_fin !== "N/A") {
          console.log('Configurando fechas del estado (novedad completa):', {
          fecha_ini: novedadCompleta.fecha_ini,
          fecha_fin: novedadCompleta.fecha_fin
        });
        
        const fechaIni = moment(novedadCompleta.fecha_ini, "YYYY-MM-DD", true);
        const fechaFin = moment(novedadCompleta.fecha_fin, "YYYY-MM-DD", true);
        
        console.log('Fechas parseadas para estado:', {
          fechaIni: { valor: fechaIni.format(), valida: fechaIni.isValid() },
          fechaFin: { valor: fechaFin.format(), valida: fechaFin.isValid() }
        });
        
        if (fechaIni.isValid() && fechaFin.isValid()) {
          setFechaRango([fechaIni, fechaFin]);
        }
      }
    }
  }, [visible, novedadCompleta, empleados, tiposNovedad, form, handleEmpleadoChange]);

  // Configurar formulario con datos de la tabla (fallback)
  useEffect(() => {
    if (visible && novedadEditar && !novedadCompleta && tiposNovedad.length > 0 && empleados.length > 0) {
      const { empleadoId } = configurarFormularioEdicion(form, novedadEditar, empleados, tiposNovedad);
      setSubirSoporte(novedadEditar.soporte);
      
      if (empleadoId) {
        handleEmpleadoChange(empleadoId);
      }
        if (novedadEditar.fecha_ini && novedadEditar.fecha_fin && 
          novedadEditar.fecha_ini !== "N/A" && novedadEditar.fecha_fin !== "N/A") {
        
        console.log('Configurando fechas del estado (fallback):', {
          fecha_ini: novedadEditar.fecha_ini,
          fecha_fin: novedadEditar.fecha_fin
        });
        
        const fechaIni = moment(novedadEditar.fecha_ini, "YYYY-MM-DD", true);
        const fechaFin = moment(novedadEditar.fecha_fin, "YYYY-MM-DD", true);
        
        console.log('Fechas parseadas para estado (fallback):', {
          fechaIni: { valor: fechaIni.format(), valida: fechaIni.isValid() },
          fechaFin: { valor: fechaFin.format(), valida: fechaFin.isValid() }
        });
        
        if (fechaIni.isValid() && fechaFin.isValid()) {
          setFechaRango([fechaIni, fechaFin]);
        }
      }
    }
  }, [visible, empleados, tiposNovedad, novedadEditar, novedadCompleta, form, handleEmpleadoChange]);

  // Manejadores
  const handleFechasChange = (fechas) => {
    setFechaRango(fechas);
    if (fechas?.[0] && fechas?.[1]) {
      const dias = fechas[1].diff(fechas[0], "days") + 1;
      form.setFieldsValue({ dias });
    }
  };
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('Valores del formulario validados:', values);
      
      setSubmitting(true);

      if (subirSoporte && !archivo) {
        toast.error("Debe adjuntar un archivo de soporte");
        return;
      }

      // Validar campos requeridos específicamente
      if (!values.empleado) {
        toast.error("Debe seleccionar un empleado");
        return;
      }

      if (!values.tipoNovedad) {
        toast.error("Debe seleccionar un tipo de novedad");
        return;
      }

      if (!fechaRango || !fechaRango[0] || !fechaRango[1]) {
        toast.error("Debe seleccionar el período de la novedad");
        return;
      }

      console.log('Estado antes de preparar datos:', {
        values,
        fechaRango,
        subirSoporte,
        modoEdicion,
        novedadEditar
      });

      const datosNovedad = prepararDatosFormulario(
        values, fechaRango, archivo, subirSoporte, modoEdicion, novedadEditar
      );

      const response = modoEdicion 
        ? await novedadesApi.update(novedadEditar.id, datosNovedad)
        : await novedadesApi.create(datosNovedad);

      if (response?.data?.status) {
        const mensaje = modoEdicion 
          ? "Novedad actualizada exitosamente" 
          : "Novedad creada exitosamente";
        toast.success(mensaje);
        
        form.resetFields();
        setArchivo(null);
        onSuccess?.();
        
        setTimeout(() => onCancel?.(), 100);
      } else {
        const errorMsg = response?.data?.message || 
          (modoEdicion ? "Error al actualizar la novedad" : "Error al crear la novedad");
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Error al enviar formulario:", error);
      const errorMessage = error?.response?.data?.message || 
        error?.message || 
        (modoEdicion ? "Error al actualizar novedad" : "Error al crear novedad");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={modoEdicion ? "Editar Novedad de Personal" : "Nueva Novedad de Personal"}
      open={visible}
      onCancel={() => {
        onCancel();
        setArchivo(null);
      }}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {modoEdicion ? "Actualizar" : "Guardar"}
        </Button>,
      ]}
    >
      <Spin spinning={loading || empleadoLoading || cargandoNovedad}>
        <Form
          form={form}
          layout="vertical"
          name="novedadForm"
          className="[&_.ant-form-item]:mb-2"
        >
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <EmpleadoFields 
              form={form}
              empleados={empleados}
              handleEmpleadoChange={handleEmpleadoChange}
              modoEdicion={modoEdicion}
            />
            
            <NovedadFields 
              tiposNovedad={tiposNovedad}
              handleFechasChange={handleFechasChange}
            />
            
            <TiempoFields />
            
            <CursoFields esAdministrativo={esAdministrativo} />
            
            <CheckboxFields setSubirSoporte={setSubirSoporte} />
          </div>

          <Form.Item
            name="observaciones"
            label="Observaciones"
            rules={[{ required: true, message: "Por favor ingrese las observaciones" }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <SoporteUpload 
            subirSoporte={subirSoporte}
            setArchivo={setArchivo}
          />
        </Form>
      </Spin>
    </Modal>
  );
};

export default FormNovedad;